// consentimiento.js — Consentimiento de cookies (RGPD/CCPA) por proyecto.
//
// La decisión del visitante se guarda en localStorage junto con la versión del
// texto legal y la fecha; si el admin sube la versión o pasa la caducidad, se
// vuelve a preguntar. Mientras no haya decisión, las categorías opcionales se
// consideran DENEGADAS (los scripts de terceros no se inyectan).
//
// Nada de esto se activa si el proyecto no tiene `config_json.consentimiento.activo`.

export const CATEGORIAS = [
  { key: 'necesarias',  label: 'Necesarias',  fija: true,
    desc: 'Imprescindibles para que el sitio funcione (sesión, seguridad, preferencias básicas). No se pueden desactivar.' },
  { key: 'preferencias', label: 'Preferencias', fija: false,
    desc: 'Recuerdan tus ajustes (idioma, región) para personalizar la experiencia.' },
  { key: 'estadisticas', label: 'Estadísticas', fija: false,
    desc: 'Nos ayudan a entender cómo se usa el sitio de forma agregada y anónima.' },
  { key: 'marketing',    label: 'Marketing',    fija: false,
    desc: 'Permiten medir campañas y mostrar contenido relevante fuera del sitio.' },
];

export const CLAVES_CATEGORIAS = CATEGORIAS.map(c => c.key);
export const EVENTO_CAMBIO = 'consentimiento-cambiado';

const claveStorage = (proyectoId) => `consentimiento_p${proyectoId || 0}`;

const todas = (valor) => CATEGORIAS.reduce((acc, c) => {
  acc[c.key] = c.fija ? true : valor;
  return acc;
}, {});

export const aceptarTodas = () => todas(true);
export const rechazarTodas = () => todas(false);

/**
 * Decisión guardada, o null si no hay / caducó / cambió la versión del texto.
 * `meses` = caducidad configurada (0 o vacío = no caduca).
 */
export const leerConsentimiento = (proyectoId, version = 1, meses = 12) => {
  try {
    const raw = window.localStorage.getItem(claveStorage(proyectoId));
    if (!raw) return null;
    const dato = JSON.parse(raw);
    if (!dato || typeof dato !== 'object') return null;
    if (Number(dato.version || 1) !== Number(version || 1)) return null;
    const m = Number(meses);
    if (m > 0 && dato.ts) {
      const vencimiento = new Date(dato.ts);
      vencimiento.setMonth(vencimiento.getMonth() + m);
      if (Date.now() > vencimiento.getTime()) return null;
    }
    return { ...dato, categorias: { ...todas(false), ...(dato.categorias || {}) } };
  } catch (e) {
    return null;
  }
};

export const guardarConsentimiento = (proyectoId, version, categorias) => {
  const dato = {
    version: Number(version || 1),
    ts: new Date().toISOString(),
    categorias: { ...todas(false), ...(categorias || {}) },
  };
  try {
    window.localStorage.setItem(claveStorage(proyectoId), JSON.stringify(dato));
  } catch (e) { /* modo privado: la decisión vale solo para esta visita */ }
  window.dispatchEvent(new CustomEvent(EVENTO_CAMBIO, { detail: dato }));
  return dato;
};

export const borrarConsentimiento = (proyectoId) => {
  try { window.localStorage.removeItem(claveStorage(proyectoId)); } catch (e) { /* noop */ }
  window.dispatchEvent(new CustomEvent(EVENTO_CAMBIO, { detail: null }));
};

/**
 * ¿Se puede cargar algo de esta categoría?
 *   • proyecto sin banner activo → sí (comportamiento de siempre)
 *   • con banner y sin decisión  → solo 'necesarias'
 */
export const permiteCategoria = (proyecto, categoria = 'necesarias') => {
  const cfg = proyecto?.config_json?.consentimiento || {};
  if (cfg.activo !== true) return true;
  if (categoria === 'necesarias' || !categoria) return true;
  const dec = leerConsentimiento(proyecto?.id, cfg.version, cfg.caducidad_meses);
  return !!dec?.categorias?.[categoria];
};

// Permite abrir el panel de preferencias desde cualquier parte (un enlace del
// footer, un ítem del menú o HTML personalizado con href="#consentimiento").
export const EVENTO_ABRIR = 'consentimiento-abrir';
export const abrirPreferencias = () => window.dispatchEvent(new CustomEvent(EVENTO_ABRIR));
